import React, { useState, useEffect, useCallback } from 'react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, ReferenceLine } from 'recharts';
import { TrendingUp, Users, Brain, AlertCircle, CheckCircle, Target, BarChart3 } from 'lucide-react';

const EnhancedResultsPage = ({ sessionId, onStartNew }) => {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [pdfLoading, setPdfLoading] = useState(false);

  const fetchResults = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/assessment/results/${sessionId}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch results: ${response.status}`);
      }

      const data = await response.json();
      setResults(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  const downloadPDF = async () => {
    try {
      setPdfLoading(true);

      const patientInfo = {
        name: 'Assessment Participant',
        age: 'Not specified'
      };

      const response = await fetch('/api/assessment/report/pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          patient_info: patientInfo
        })
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`Failed to generate PDF: ${response.status} - ${errorData}`);
      }

      const data = await response.json();

      const byteCharacters = atob(data.pdf_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('PDF download failed:', error);
      alert(`Failed to generate PDF report: ${error.message}`);
    } finally {
      setPdfLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-cyan-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-lg text-gray-600">Analyzing your results...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-cyan-100 flex items-center justify-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
            <h3 className="text-lg font-medium text-red-800">Error Loading Results</h3>
          </div>
          <p className="mt-2 text-red-700">{error}</p>
          <button
            onClick={onStartNew}
            className="mt-4 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
          >
            Start New Assessment
          </button>
        </div>
      </div>
    );
  }

  const { assessment_summary, percentile_feedback, severity_assessment, distribution_visualization, predicted_responses } = results;

  const ScoreDistributionChart = () => {
    const data = distribution_visualization.distribution_curve.map(point => ({
      x: point.x,
      y: point.y,
      isInRange: point.x >= distribution_visualization.confidence_bounds.lower &&
                 point.x <= distribution_visualization.confidence_bounds.upper
    }));

    return (
      <div className="bg-white rounded-lg p-6 shadow-lg">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <TrendingUp className="mr-2 h-5 w-5 text-teal-600" />
          Your Score on the Population Distribution
        </h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="x"
                domain={[-3, 3]}
                type="number"
                tickFormatter={(value) => value.toFixed(1)}
              />
              <YAxis hide />
              <Tooltip
                formatter={(value) => [value.toFixed(4), 'Probability Density']}
                labelFormatter={(value) => `Score: ${value.toFixed(2)}`}
              />
              <Area
                type="monotone"
                dataKey="y"
                stroke="#0d9488"
                fill="#99f6e4"
                fillOpacity={0.6}
              />
              <ReferenceLine
                x={distribution_visualization.user_score}
                stroke="#dc2626"
                strokeWidth={3}
                label={{ value: "Your Score", position: "top" }}
              />
              <ReferenceLine
                x={distribution_visualization.confidence_bounds.lower}
                stroke="#f97316"
                strokeDasharray="5 5"
              />
              <ReferenceLine
                x={distribution_visualization.confidence_bounds.upper}
                stroke="#f97316"
                strokeDasharray="5 5"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 text-sm text-gray-600">
          <div className="flex items-center justify-between">
            <span>Low OCD Symptoms</span>
            <span>High OCD Symptoms</span>
          </div>
          <div className="mt-2 text-center">
            <span className="inline-block w-3 h-3 bg-red-600 mr-2"></span>
            Your Score: {assessment_summary.theta_score}
            <span className="ml-4 inline-block w-3 h-3 bg-orange-400 mr-2"></span>
            95% Confidence Range
          </div>
        </div>
      </div>
    );
  };

  const DetailedItemPredictions = () => {
    return (
      <div className="bg-white rounded-lg p-6 shadow-lg">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <BarChart3 className="mr-2 h-5 w-5 text-teal-600" />
          Predicted Responses to All {assessment_summary.total_available_items} Items
        </h3>
        <p className="text-gray-600 mb-6">
          Based on your theta score, here are your predicted responses to all items in the OCD-CAT item bank:
        </p>

        <div className="space-y-4">
          {predicted_responses.all_predictions.map((prediction, index) => (
            <div key={index} className="border rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-800 mb-1">
                    Item {prediction.item_id + 1}
                  </h4>
                  <p className="text-sm text-gray-600 mb-2">
                    {prediction.question}
                  </p>
                </div>
                <div className="text-right ml-4">
                  <span className="inline-block px-3 py-1 bg-teal-100 text-teal-800 rounded-full text-sm font-medium">
                    {prediction.predicted_response}
                  </span>
                  <p className="text-xs text-gray-500 mt-1">
                    {(prediction.confidence * 100).toFixed(1)}% confidence
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <div className="text-xs text-gray-500 mb-1">Response Probabilities:</div>
                {Object.entries(prediction.probabilities).map(([response, prob]) => (
                  <div key={response} className="flex items-center">
                    <span className="w-16 text-xs text-gray-600">{response}</span>
                    <div className="flex-1 mx-2">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-teal-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${prob * 100}%` }}
                        ></div>
                      </div>
                    </div>
                    <span className="w-12 text-xs text-gray-500 text-right">
                      {(prob * 100).toFixed(1)}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-cyan-100">
      <div className="container mx-auto px-4 py-8">

        {/* Header Section */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Your OCD-CAT Results
          </h1>
          <p className="text-gray-600">
            Comprehensive analysis of your OCD symptom assessment
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">

          {/* Score Card */}
          <div className="bg-white rounded-lg p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Your Score</h3>
                <p className="text-3xl font-bold text-teal-600">
                  {assessment_summary.theta_score}
                </p>
                <p className="text-sm text-gray-600">
                  ± {assessment_summary.standard_error} (SE)
                </p>
              </div>
              <Target className="h-12 w-12 text-teal-600" />
            </div>
          </div>

          {/* Percentile Card */}
          <div className="bg-white rounded-lg p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Percentile Rank</h3>
                <p className="text-3xl font-bold text-green-600">
                  {percentile_feedback.percentile_ordinal}
                </p>
                <p className="text-sm text-gray-600">
                  ({percentile_feedback.confidence_interval.lower_percentile.toFixed(1)}% - {percentile_feedback.confidence_interval.upper_percentile.toFixed(1)}%)
                </p>
              </div>
              <Users className="h-12 w-12 text-green-600" />
            </div>
          </div>

          {/* Severity Card */}
          <div className="bg-white rounded-lg p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Severity Level</h3>
                <p
                  className="text-3xl font-bold"
                  style={{ color: severity_assessment.color }}
                >
                  {severity_assessment.level}
                </p>
                <p className="text-sm text-gray-600">
                  {assessment_summary.items_completed} items completed
                </p>
              </div>
              <Brain className="h-12 w-12" style={{ color: severity_assessment.color }} />
            </div>
          </div>
        </div>

        {/* Detailed Explanation */}
        <div className="bg-white rounded-lg p-6 shadow-lg mb-8">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <CheckCircle className="mr-2 h-5 w-5 text-green-600" />
            What Your Results Mean
          </h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">Percentile Interpretation</h4>
              <p className="text-gray-700 mb-4">
                Your OCD symptom score places you in the <strong>{percentile_feedback.percentile_ordinal} percentile</strong> based
                on a community sample. This means you scored higher than {percentile_feedback.percentile.toFixed(1)}% of people
                in the general population.
              </p>
              <p className="text-gray-600 text-sm">
                We are 95% confident that your true score lies between the <strong>
                {percentile_feedback.confidence_interval.lower_percentile.toFixed(1)}th</strong> and <strong>
                {percentile_feedback.confidence_interval.upper_percentile.toFixed(1)}th</strong> percentiles.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">Clinical Interpretation</h4>
              <p className="text-gray-700 mb-4">
                {severity_assessment.description}
              </p>
              <div className="space-y-2">
                <h5 className="font-medium text-gray-800">Recommendations:</h5>
                <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                  {severity_assessment.recommendations.map((rec, index) => (
                    <li key={index}>{rec}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('overview')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'overview'
                    ? 'border-teal-500 text-teal-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Score Distribution
              </button>
              <button
                onClick={() => setActiveTab('detailed')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'detailed'
                    ? 'border-teal-500 text-teal-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                All Item Predictions
              </button>
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="mb-8">
          {activeTab === 'overview' && <ScoreDistributionChart />}
          {activeTab === 'detailed' && <DetailedItemPredictions />}
        </div>

        {/* Action Buttons */}
        <div className="text-center space-y-4">
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              onClick={downloadPDF}
              disabled={pdfLoading}
              className={`${
                pdfLoading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700'
              } text-white px-8 py-3 rounded-lg transition-colors flex items-center gap-2`}
            >
              {pdfLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Generating PDF...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download Assessment Report (PDF)
                </>
              )}
            </button>

            <button
              onClick={onStartNew}
              className="bg-teal-600 text-white px-8 py-3 rounded-lg hover:bg-teal-700 transition-colors"
            >
              Start New Assessment
            </button>
          </div>

          <p className="text-sm text-gray-600">
            The PDF report includes your scores, severity interpretation, and clinical recommendations.
          </p>
        </div>

      </div>
    </div>
  );
};

export default EnhancedResultsPage;
